'use client';

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { cn } from '@/core/utils/cn';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Props                                                                      */
/* -------------------------------------------------------------------------- */

interface PhotoLightboxProps {
  photos: string[];
  initialIndex: number;
  onClose: () => void;
}

/* -------------------------------------------------------------------------- */
/*  PhotoLightbox                                                              */
/* -------------------------------------------------------------------------- */

export function PhotoLightbox({ photos, initialIndex, onClose }: PhotoLightboxProps) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(initialIndex);

  const goNext = useCallback(() => {
    setIndex((prev) => (prev + 1) % photos.length);
  }, [photos.length]);

  const goPrev = useCallback(() => {
    setIndex((prev) => (prev - 1 + photos.length) % photos.length);
  }, [photos.length]);

  /* Keyboard navigation */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, goNext, goPrev]);

  return (
    <div id="hotels-lightbox" className="fixed inset-0 z-50 flex flex-col bg-black/95">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3">
        <span id="hotels-lightbox-counter" className="text-sm font-medium text-white/70">
          {index + 1} / {photos.length}
        </span>
        <button
          id="hotels-lightbox-close"
          type="button"
          onClick={onClose}
          className="rounded-full p-2 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Main image area */}
      <div className="relative flex flex-1 items-center justify-center px-14 min-h-0">
        <button
          id="hotels-lightbox-prev"
          type="button"
          onClick={goPrev}
          className="absolute left-3 z-10 rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20 transition-colors"
        >
          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <img
          src={photos[index]}
          alt={`Photo ${index + 1}`}
          className="max-h-full max-w-full object-contain"
        />
        <button
          id="hotels-lightbox-next"
          type="button"
          onClick={goNext}
          className="absolute right-3 z-10 rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20 transition-colors"
        >
          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      {/* Thumbnail strip */}
      <div className="flex items-center justify-center gap-2 overflow-x-auto px-4 py-3">
        {photos.map((photo, i) => (
          <button
            key={i}
            id={`hotels-lightbox-thumb-${i}`}
            type="button"
            onClick={() => setIndex(i)}
            className={cn(
              'relative h-14 w-20 shrink-0 overflow-hidden rounded-lg transition-all',
              i === index ? 'ring-2 ring-hms-gold opacity-100' : 'opacity-50 hover:opacity-80',
            )}
          >
            <Image src={photo} alt={`Thumbnail ${i + 1}`} fill sizes="80px" className="object-cover" unoptimized />
          </button>
        ))}
      </div>
    </div>
  );
}
